import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import {
  getMobileGenreMarketRankData,
  getMobilePageData,
  getPageData,
  TGranularity,
} from '@/pages/intelligence/services/marketProfile';
import {
  sortTime,
  formatEnTimeByStr,
  pcHasDataCountry,
  getUrlParam,
} from '@/pages/intelligence/pages/MarketProfile/_util';
import { history } from 'umi';
import {
  replaceRankDates,
  safeNumberDiff,
  totalMarketMobileCardHistoryPatch,
  trans2MarketTrendData,
  trans2MobileMarketRankData,
  trans2MobileTopGenre,
  trans2SensorGenresParams,
} from './util';

export default function useOverviewModal(props: any) {
  const { marketName, countryId } = props || {};
  const [countryFlag, setCountryFlag] = useState('');

  // 筛选条件
  const [mainSegment, setMainSegment] = useState('mobile');

  const [time, setTime] = useState(['']);
  const [dataSource, setDataSource] = useState('sensortower');
  const [tempValue, setTempValue] = useState(['']);

  const [pcConsoleDisable, setPcConsoleDisable] = useState(false);

  // summary部分的参数
  const [summaryDataLoading, setSummaryDataLoading] = useState(true);
  const [part1Data, setPart1Data] = useState({});
  const [part2Data, setPart2Data] = useState({});
  const [part3Data, setPart3Data] = useState({});
  const [summaryTime, setSummaryTime] = useState('');

  // New Blockbuster Games部分
  const [newGameLoad, setNewGameLoad] = useState(true);
  const [newGameData, setNewGameData] = useState([]);
  const [newGameDate, setNewGameDate] = useState('');

  // top game & company部分
  const [topGameDataLoad, setTopGameDataLoad] = useState(true);
  const [topMobileGame10, setTopMobileGame10] = useState({});
  const [topPCGame10, setTopPCGame10] = useState({});
  const [topConsoleGame10, setTopConsoleGame10] = useState({});
  const [topTotalGame, setTopTotalGame] = useState({});
  const [topGameTime, setTopGameTime] = useState('');
  const [topGameTime2, setTopGameTime2] = useState('');
  const [topGameTime3, setTopGameTime3] = useState('');
  const [topGameTime4, setTopGameTime4] = useState('');

  const [pcHasData, setPcHasData] = useState(true);
  const [consoleHasData, setConsoleHasData] = useState(true);

  // top genre 部分
  const [topGenreDataLoad, setTopGenreDataLoad] = useState(true);
  const [topGenreData, setTopGenreData] = useState({});

  const countryList = ['us', 'de', 'gb', 'fr', 'es'];

  const dataSearch = async (param, flag = false) => {
    setSummaryDataLoading(true);
    try {
      const newGameResult = await newGameSearch(param, flag);
      const topGameResult = await topGameAndCompany(param, flag);

      // 可并行请求
      const parallelPromises = [];
      parallelPromises.push(summarySearch(param, flag));

      if (param.entity_type === 'mobile') {
        parallelPromises.push(treeMapDataDeal(param, flag));
      }

      const results = await Promise.allSettled(parallelPromises);

      const errors = results
        .filter((result) => result.status === 'rejected')
        .map((result) => result.reason);

      if (errors.length > 0) {
        console.error('dataSearch batch invoked err:', errors);
      }

      return {
        newGameResult,
        topGameResult,
        parallelResults: results.map((r) => (r.status === 'fulfilled' ? r.value : {})),
      };
    } catch (error) {
      console.error('the sequential invoked err:', error);
      throw error;
    } finally {
      setSummaryDataLoading(false);
    }
  };

  /**
   * summary模块数据查询
   */
  type PlatformType = 'mobile' | 'pc' | 'console' | 'total';

  type SummaryData = {
    revenue?: number;
    revenuePrecent?: number;
    rank: number;
    rankChange: number;
    xAxis: string[];
    yAxis: Array<{ name: string; value: number | string }>;
  };

  interface DataStrategy {
    processTotalData(totalData: any): SummaryData[];
    processTrendData(trendData: any[]): SummaryData[];
  }

  // 预处理参数
  const processParams = (params: any, isAnnie?: number | string) => {
    if (params.entity_type === 'mobile' && isAnnie) {
      return { ...params, is_annie: Number(isAnnie) === 1 };
    }
    return params;
  };

  const fetchData = async ({
    processedParams,
    lineParams,
    rankParams,
  }: {
    processedParams: any;
    lineParams: any;
    rankParams: any;
  }) => {
    let newSummaryRes = {};
    let finalSummaryRes = {};

    const summaryRes = await getPageData('detail/overview_summary', processedParams);

    const isMobileStrategy = processedParams?.entity_type === 'mobile';
    const isTotalMarketStrategy = processedParams?.entity_type === 'total';
    const isPCAndConsoleStrategy =
      processedParams?.entity_type === 'pc' || processedParams?.entity_type === 'console';

    // console.debug('needReplace', { processedParams });

    if (isTotalMarketStrategy) {
      finalSummaryRes = {
        ...summaryRes,
        mobile_summary_date: totalMarketMobileCardHistoryPatch(summaryRes?.mobile_summary_date),
      };
    }

    if (isMobileStrategy) {
      const { data: trendRes } = await getMobilePageData(lineParams);

      const updatedRankParams = replaceRankDates({
        rankParams,
        newCurDate: summaryRes?.end_date || '',
        newLastDate: moment(summaryRes?.start_date).add(-1, 'month').format('YYYY-MM-DD') || '',
      });

      const { data: rankRes } = await getMobileGenreMarketRankData(updatedRankParams);

      const { rev_rank, pre_rev_rank, dl_rank, pre_dl_rank } =
        trans2MobileMarketRankData(rankRes) || {};

      newSummaryRes = {
        rankData: { rev_rank, pre_rev_rank, dl_rank, pre_dl_rank },
        trendData: trans2MarketTrendData(trendRes?.metric_value_list),
      };

      finalSummaryRes = {
        ...summaryRes,
        mobile_summary_date: newSummaryRes?.trendData,
        mobile_summary_total: { ...summaryRes?.mobile_summary_total, ...newSummaryRes?.rankData },
      };
    }

    if (isPCAndConsoleStrategy) {
      finalSummaryRes = {
        ...summaryRes,
      };
    }

    return {
      summaryRes,
      newSummaryRes,
      finalSummaryRes,
    };
  };

  const createBaseSummaryData = (): SummaryData => ({
    revenue: 0,
    revenuePrecent: 0,
    rank: 0,
    rankChange: 0,
    xAxis: [],
    yAxis: [],
  });

  // Mobile 数据处理策略
  class MobileDataStrategy implements DataStrategy {
    processTotalData(totalData: any): SummaryData[] {
      const part1 = { ...createBaseSummaryData() };
      const part2 = { ...createBaseSummaryData() };

      part1.revenue = totalData.rev_total;
      part1.revenuePrecent = computeChange(totalData.rev_total, totalData.pre_rev_total);

      part1.rank = totalData.rev_rank;
      part1.rankChange = totalData.pre_rev_rank - totalData.rev_rank;

      part2.revenue = totalData.downLoad_total;
      part2.revenuePrecent = computeChange(totalData.downLoad_total, totalData.pre_download_total);

      part2.rank = totalData.dl_rank;
      part2.rankChange = totalData.pre_dl_rank - totalData.dl_rank;

      // console.debug('mobile totalDt :', { part1, part2, totalData });

      return [part1, part2];
    }

    processTrendData(trendData: any[]): SummaryData[] {
      const processItem = (value: number, name: string) => {
        return {
          name,
          value: value !== 0 ? Math.floor(value) : '-',
        };
      };

      const dates = trendData.map((item) => moment(item.date).format('YYYY-MM'));

      const res = [
        {
          ...createBaseSummaryData(),
          xAxis: dates,
          yAxis: trendData.map((item) => processItem(item.rev, 'Revenue($)')),
          // yAxis: trendData.map((item) => processItem(item.rev, 'Mobile Revenue($)')),
        },
        {
          ...createBaseSummaryData(),
          xAxis: dates,
          yAxis: trendData.map((item) => processItem(item.downLoad, 'Downloads')),
        },
      ];

      // console.debug('mobile trendDt :', { res });

      return res;
    }
  }

  // PC/Console 数据处理策略
  class PcConsoleDataStrategy implements DataStrategy {
    processTotalData(totalData: any): SummaryData[] {
      const part1 = { ...createBaseSummaryData() };
      const part2 = { ...createBaseSummaryData() };

      // console.debug('inPcConsole Strategy', { part1, part2, totalData });

      // 数字收入数据处理
      part1.revenue = totalData.digtal_rev_total;
      part1.revenuePrecent = safeNumberDiff({
        curVal: totalData.digtal_rev_total,
        preVal: totalData.pre_digtal_rev_total,
      })
        ? computeChange(totalData.digtal_rev_total, totalData.pre_digtal_rev_total)
        : 0;

      part1.rank = totalData.dig_rev_rank;
      part1.rankChange = safeNumberDiff({
        preVal: totalData.pre_dig_rev_rank,
        curVal: totalData.dig_rev_rank,
      });

      // 实体收入数据处理
      part2.revenue = totalData.physical_rev_total;

      part2.revenuePrecent = safeNumberDiff({
        curVal: totalData.physical_rev_total,
        preVal: totalData.pre_physical_rev_total,
      })
        ? computeChange(totalData.physical_rev_total, totalData.pre_physical_rev_total)
        : 0;

      part2.rank = totalData.phy_rank;
      part2.rankChange = safeNumberDiff({
        preVal: totalData.pre_phy_rank,
        curVal: totalData.phy_rank,
      });

      return [part1, part2];
    }

    processTrendData(trendData: any[]): SummaryData[] {
      const processItem = (value: number, name: string) => ({
        name,
        value: value !== 0 ? Math.floor(value) : '-',
      });

      const dates = trendData.map((item) => moment(item.date).format('YYYY-MM'));
      return [
        {
          ...createBaseSummaryData(),
          xAxis: dates,
          yAxis: trendData.map((item) => processItem(item.digtal_rev, 'Digital Revenue($)')),
        },
        {
          ...createBaseSummaryData(),
          xAxis: dates,
          yAxis: trendData.map((item) => processItem(item.physical_rev, 'Physical Revenue($)')),
        },
      ];
    }
  }

  // Total 数据处理策略
  class TotalDataStrategy implements DataStrategy {
    processTotalData(totalData: any): SummaryData[] {
      const res: SummaryData[] = [];
      const platforms = ['mobile', 'pc', 'console'] as const;

      platforms.forEach((platform) => {
        const data = { ...createBaseSummaryData() };
        // 原有的特殊处理的逻辑，mobile 反而不取对应前缀的值
        // const prefix = platform === 'mobile' ? '' : `${platform}_`;
        const prefix = `${platform}_`;

        data.revenue = totalData[`${prefix}rev_total`];

        data.revenuePrecent = computeChange(
          totalData[`${prefix}rev_total`],
          totalData[`pre_${prefix}rev_total`],
        );

        data.rank = totalData[`${prefix}rev_rank`];
        data.rankChange = totalData[`${prefix}pre_rev_rank`] - totalData[`${prefix}rev_rank`];

        // console.debug('totalDt total res for in', { data, prefix });

        res.push(data);
      });

      // console.debug('totalDt total res', { res, totalData });

      return res;
    }

    processTrendData(trendData: any[]): SummaryData[] {
      // console.debug('trend in total', trendData);

      const processItem = (value: number, name: string) => {
        return {
          name,
          // value,
          value: value !== 0 ? Math.floor(value) : '-',
        };
      };

      const dates = trendData.map((item) => moment(item.date).format('YYYY-MM'));

      const res = [
        {
          ...createBaseSummaryData(),
          xAxis: dates,
          yAxis: trendData.map((item) => {
            // console.debug('intd', item);
            return processItem(item.mobile_rev, 'Mobile Revenue($)');
          }),
        },
        {
          ...createBaseSummaryData(),
          xAxis: dates,
          yAxis: trendData.map((item) => processItem(item.pc_rev, 'PC Revenue($)')),
        },
        {
          ...createBaseSummaryData(),
          xAxis: dates,
          yAxis: trendData.map((item) => processItem(item.console_rev, 'Console Revenue($)')),
        },
      ];

      // console.debug('totalDt trend res', { res, trendData });
      return res;
    }
  }

  // 策略工厂
  const getDataStrategy = (platform: PlatformType): DataStrategy => {
    // console.debug('platform in getDataS', { platform });

    switch (platform) {
      case 'mobile':
        return new MobileDataStrategy();
      case 'pc':
        return new PcConsoleDataStrategy();
      case 'console':
        return new PcConsoleDataStrategy();
      case 'total':
        return new TotalDataStrategy();
    }
  };

  const summarySearch = async (params: any, flag: boolean) => {
    try {
      setSummaryDataLoading(true);

      const { market, start_date, end_date, source } = params;
      const metrics = ['revenue', 'download'];
      const granularity = 'monthly';

      // 参数处理
      const isAnnie = history.location.query?.isAnnie;
      const processedParams = processParams(params, isAnnie);

      const lineParams = {
        source_metrics: { [source]: metrics },
        granularity,
        regions: [market],
        platforms: ['All'],
        metrics,
        genres: ['All'],
        b_growth_ratio: true,
        b_group_by_date: true,
        b_group_by_market: true,
        start_date: start_date ? moment(start_date)?.add(-5, 'month').format('YYYY-MM-DD') : '',
        end_date,
      };

      const rankParams = {
        market,
        rank_date_map: [
          {
            metric_name: 'revenue',
            cur_date: start_date,
            last_date: end_date,
            granularity,
            data_source: source,
          },
          {
            metric_name: 'download',
            cur_date: start_date,
            last_date: end_date,
            granularity,
            data_source: source,
          },
        ],
      };

      const { summaryRes, newSummaryRes, finalSummaryRes } = await fetchData({
        processedParams,
        lineParams,
        rankParams,
      });

      // if (!finalSummaryRes) return;

      // 获取数据处理策略
      const platform = params.entity_type as PlatformType;
      const strategy = getDataStrategy(platform);
      const totalMarketPatchMobileStrategy = getDataStrategy('total');

      // console.debug('strategy invoked', {
      //   platform,
      //   summaryRes,
      //   newSummaryRes,
      //   finalSummaryRes,
      // });

      // 核心数据处理
      // const totalData = extractTotalData(summaryRes, platform);
      // const trendData = extractTrendData(summaryRes, platform);

      const totalData = extractTotalData(finalSummaryRes, platform);
      const trendData = extractTrendData(finalSummaryRes, platform);

      const totalMarketMobileTrendData = extractTrendData(finalSummaryRes, 'total')?.map(
        (item, idx) => {
          const patch = finalSummaryRes?.mobile_summary_date?.[idx];

          return {
            ...item,
            mobile_rev: patch?.rev,
          };
        },
      );

      const totalParts = strategy.processTotalData(totalData);
      const trendParts = strategy.processTrendData(trendData);

      const totalMarketMobileTrendParts = totalMarketPatchMobileStrategy.processTrendData(
        totalMarketMobileTrendData,
      );

      // console.debug('fetch Summary', {
      //   platform,
      //   trendData,
      //   trendParts,
      //   totalMarketMobileTrendData,
      //   totalMarketMobileTrendParts,
      //   params,
      //   processedParams,
      //   lineParams,
      //   summaryRes,
      //   newSummaryRes,
      //   finalSummaryRes,
      // });

      // 合并基础数据与趋势数据
      const finalData1 = {
        ...totalParts[0],
        xAxis: trendParts[0].xAxis,
        // yAxis: trendParts[0].yAxis,
        yAxis: platform === 'total' ? totalMarketMobileTrendParts[0].yAxis : trendParts[0].yAxis,
      };

      const finalData2 = {
        ...totalParts[1],
        xAxis: trendParts[1].xAxis,
        yAxis: trendParts[1].yAxis,
      };
      const finalData3 =
        platform === 'total'
          ? {
              ...totalParts[2],
              xAxis: trendParts[2].xAxis,
              yAxis: trendParts[2].yAxis,
            }
          : null;

      // 批量更新状态

      const state = {
        part1Data: finalData1,
        part2Data: finalData2,
        part3Data: platform === 'total' ? finalData3 : {},
        // summaryTime: summaryRes.end_date,
        summaryTime: finalSummaryRes.end_date,
        flag,
      };

      // console.debug('batchUpdate:', { state });

      // 批量更新状态
      batchUpdateStates(state);
    } catch (error) {
      console.error('Summary数据获取失败:', error);
    } finally {
      setSummaryDataLoading(false);
    }
  };

  // 几个辅助函数

  // 批量更新状态
  const batchUpdateStates = ({ part1Data, part2Data, part3Data, summaryTime, flag }: any) => {
    setPart1Data(part1Data);
    setPart2Data(part2Data);
    setPart3Data(part3Data);

    // console.debug('batchUpdate in', { part1Data, part2Data, summaryTime, flag });

    if (summaryTime && flag) {
      setSummaryTime(summaryTime);
      changeDefaultTime(summaryTime !== '1970-01-01' ? summaryTime : moment().format('YYYY-MM-DD'));
      onChangeRangePicker([moment(summaryTime), moment(summaryTime)], false);
    }
  };

  // 抽象 totalData
  const extractTotalData = (data: any, platform: PlatformType) => {
    const PLAT_MAP = {
      mobile: 'mobile_summary_total',
      pc: 'pc_summary_total',
      console: 'console_summary_total',
      total: 'total_all',
    };

    const dataKey = PLAT_MAP[platform];
    const res = data[dataKey];

    return res;
  };

  // 抽象 trendData
  const extractTrendData = (data: any, platform: PlatformType) => {
    const PLAT_MAP = {
      mobile: 'mobile_summary_date',
      pc: 'pc_summary_date',
      console: 'console_summary_date',
      total: 'total_summary_date',
    };

    const dataKey = PLAT_MAP[platform];
    const res = data[dataKey]?.sort(sortTime('date')) || [];

    return res;
  };

  /**
   * New Blockbuster Games模块数据查询
   */
  const newGameSearch = async (parma, flag) => {
    setNewGameLoad(true);

    const newGameRes = await getPageData('detail/overview_new_blockbuster_game', parma);

    let data = [];

    if (newGameRes) {
      const gameList =
        newGameRes[
          parma.entity_type == 'mobile'
            ? 'nbb_game'
            : parma.entity_type == 'pc'
            ? 'pc_nbb_game'
            : parma.entity_type == 'console'
            ? 'console_nbb_game'
            : 'total_nbb_game'
        ];
      if (gameList) {
        gameList.map((item) => {
          item.countryId = parma.market;
        });
        data = gameList;
        const end_date = newGameRes?.end_date;
        if (end_date && flag) {
          setNewGameDate(end_date != '1970-01-01' ? end_date : moment().format('YYYY-MM-DD'));
          changeDefaultTime(end_date != '1970-01-01' ? end_date : moment().format('YYYY-MM-DD'));
        }
      }
      setNewGameData(data);
      setNewGameLoad(false);
    } else {
      setNewGameData([]);
      setNewGameLoad(false);
    }
  };

  /**
   * top games & company模块数据查询
   */
  const topGameAndCompany = async (parma, flag) => {
    setTopGameDataLoad(true);
    // let topGameRes = await getPageData('detail/overview_tops?cacheRefresh=true', parma);
    const topGameRes = await getPageData('detail/overview_tops', parma);
    if (topGameRes && parma.entity_type == 'mobile') {
      const data = topGameRes.mobile;
      data.countryId = parma.market;
      setTopMobileGame10(data);
      const end_date = topGameRes?.app_end_date;
      const end_date2 = topGameRes?.com_end_date;
      if (end_date && flag && end_date != '1970-01-01') {
        setTopGameTime(end_date);
        setTopGameTime2(end_date2);
        changeDefaultTime(end_date != '1970-01-01' ? end_date : moment().format('YYYY-MM-DD'));
        changeDefaultTime(end_date2 != '1970-01-01' ? end_date2 : moment().format('YYYY-MM-DD'));
      }
    } else if (topGameRes && parma.entity_type == 'pc') {
      const data = topGameRes.pc;
      data.countryId = parma.market;
      setTopPCGame10(data);
      const end_date = topGameRes?.pc_end_date;
      const end_date2 = topGameRes?.pc_com_end_date;
      if (end_date && flag && end_date != '1970-01-01') {
        setTopGameTime(end_date);
        setTopGameTime2(end_date2);
        changeDefaultTime(end_date != '1970-01-01' ? end_date : moment().format('YYYY-MM-DD'));
        changeDefaultTime(end_date2 != '1970-01-01' ? end_date2 : moment().format('YYYY-MM-DD'));
      }
    } else if (topGameRes && parma.entity_type == 'console') {
      const data = topGameRes.console;
      data.countryId = parma.market;
      setTopConsoleGame10(data);
      const end_date = topGameRes?.console_end_date;
      const end_date2 = topGameRes?.console_com_end_date;
      if (end_date && flag && end_date != '1970-01-01') {
        setTopGameTime(end_date);
        setTopGameTime2(end_date2);
        changeDefaultTime(end_date != '1970-01-01' ? end_date : moment().format('YYYY-MM-DD'));
        changeDefaultTime(end_date2 != '1970-01-01' ? end_date2 : moment().format('YYYY-MM-DD'));
      }
    } else if (topGameRes && parma.entity_type == 'total') {
      const data = topGameRes.totals || {};
      data.countryId = parma.market;
      setTopTotalGame(data);
      const mobile_end_date = topGameRes?.app_end_date;
      const pc_end_date = topGameRes?.pc_end_date;
      const console_end_date = topGameRes?.console_end_date;
      const com_end_date = topGameRes?.com_end_date;

      if (!data.pc_tops && !data.pc_game_trend) {
        setPcHasData(false);
      }

      if (!data.console_tops && !data.console_Game_trend) {
        setConsoleHasData(false);
      }

      if (flag) {
        setTopGameTime(mobile_end_date != '1970-01-01' ? mobile_end_date : '');
        setTopGameTime2(pc_end_date != '1970-01-01' ? pc_end_date : '');
        setTopGameTime3(console_end_date != '1970-01-01' ? console_end_date : '');
        setTopGameTime4(com_end_date != '1970-01-01' ? com_end_date : '');

        changeDefaultTime(
          mobile_end_date != '1970-01-01' ? mobile_end_date : moment().format('YYYY-MM-DD'),
        );
        changeDefaultTime(
          pc_end_date != '1970-01-01' ? pc_end_date : moment().format('YYYY-MM-DD'),
        );
        changeDefaultTime(
          console_end_date != '1970-01-01' ? console_end_date : moment().format('YYYY-MM-DD'),
        );
        changeDefaultTime(
          com_end_date != '1970-01-01' ? com_end_date : moment().format('YYYY-MM-DD'),
        );
      }
    }

    setTopGameDataLoad(false);
  };

  const treeMapDataDeal = async (param, flag) => {
    setTopGenreDataLoad(true);
    const genreData = await getPageData('overview/top_genre', param); // 调试新接口, youbing

    // const genreData = await getPageData('detail/overview_top_genre', param);

    const newGenreParams = trans2SensorGenresParams(param);
    const { data: rawGenreData } = await getMobilePageData(newGenreParams);
    const newGenreData = trans2MobileTopGenre(rawGenreData?.metric_value_list);

    // console.debug('in genre Data', {
    //   param,
    //   newGenreParams,
    //   genreData,
    //   newGenreData,
    //   rawGenreData,
    // });

    // if (genreData) {
    //   setTopGenreData(genreData);

    //   if (flag && genreData?.end_date) {
    //     changeDefaultTime(
    //       genreData.end_date != '1970-01-01' ? genreData.end_date : moment().format('YYYY-MM-DD'),
    //     );
    //   }
    // }

    if (newGenreData) {
      setTopGenreData(newGenreData);

      if (flag && newGenreData?.end_date) {
        changeDefaultTime(
          newGenreData.end_date != '1970-01-01'
            ? newGenreData.end_date
            : moment().format('YYYY-MM-DD'),
        );
      }
    }

    setTopGenreDataLoad(false);
  };

  // 根据各个模块最新时间设置显示时间
  const changeDefaultTime = (time) => {
    let timeD = '';
    if (tempValue.length == 0) {
      timeD = time;
    } else {
      timeD = tempValue[0] == '' ? moment().format('YYYY-MM-DD') : tempValue[0];
      const diff =
        new Date(time.split('-').join('/')).getTime() -
        new Date(timeD.split('-').join('/')).getTime();
      if (diff < 0) {
        timeD = time;
      }
    }
    tempValue.length = 0;
    tempValue.push(...[timeD]);
    setTempValue(tempValue);
    // 修改时间赋值格式，feat: 页面刷新时应该赋值到筛选器
    if (moment(timeD).isValid()) {
      setTime([moment(timeD), moment(timeD)]);
    }
  };

  const formatLineChartDate = (value) => {
    const monthDic = {
      '01': 'Jan',
      '02': 'Feb',
      '03': 'Mar',
      '04': 'Apr',
      '05': 'May',
      '06': 'Jun',
      '07': 'Jul',
      '08': 'Aug',
      '09': 'Sep',
      10: 'Oct',
      11: 'Nov',
      12: 'Dec',
    };
    const dateArr = value?.split('-');
    return `${monthDic[dateArr[1]]}, ${dateArr[0].substr(2, 2)}`;
  };

  const computeChange = (nowNumber, preNumber) => {
    if (nowNumber == 0) {
      return 0;
    }
    if (nowNumber != 0 && preNumber == 0) {
      return 1;
    }
    if (nowNumber != 0 && preNumber != 0) {
      return (nowNumber - preNumber) / preNumber;
    }
  };

  // 切换市场源
  const onSegmentChange = (value) => {
    const needSource = value === 'total' || value === 'mobile';

    const _params = {
      market: countryId,
      entity_type: value,
      start_date: '',
      end_date: '',
      source: needSource ? dataSource : undefined,
    };

    if (mainSegment != value) {
      tempValue.length = 0;
      tempValue.push(...[moment().format('YYYY-MM-DD')]);
      setTempValue(tempValue);
      setTime(['']);
      dataSearch(_params, true);
      setMainSegment(value, true);
    }
  };

  const onChangeRangePicker = (Date: any, needFetch = true) => {
    const startDate = moment(Date[0]).format('YYYY-MM-DD');
    // console.debug('startData',startDate,'Date',Date);

    const needSource = mainSegment === 'total' || mainSegment === 'mobile';

    const _params = {
      market: countryId,
      entity_type: mainSegment,
      start_date: startDate,
      end_date: startDate,
      source: needSource ? dataSource : undefined,
    };
    // @ts-ignore
    setSummaryTime(startDate);
    // @ts-ignore
    setTime(Date);
    // @ts-ignore
    setNewGameDate(startDate);
    // @ts-ignore
    setTopGameTime(startDate);
    // @ts-ignore
    setTopGameTime2(startDate);
    // @ts-ignore
    setTopGameTime3(startDate);
    // @ts-ignore
    setTopGameTime4(startDate);
    needFetch && dataSearch(_params, false);
  };

  const onChangeDataSource = (value: string) => {
    setDataSource(value);
    const dateFormat = 'YYYY-MM-DD';
    const startDate = moment(time?.[0]).format(dateFormat);

    setDataSource(value);

    const params = {
      market: countryId,
      entity_type: mainSegment,
      start_date: startDate,
      end_date: startDate,
      source: value,
    };

    dataSearch(params, false);
  };

  return {
    dataSearch,
    setCountryFlag,
    countryFlag,
    countryList,
    setMainSegment,
    time,
    setTime,
    dataSource,
    setDataSource,
    onChangeRangePicker,
    onChangeDataSource,
    summaryDataLoading,
    summaryTime,
    part1Data,
    mainSegment,
    // actionFun,
    part2Data,
    part3Data,
    onSegmentChange,
    newGameData,
    newGameLoad,
    newGameDate,
    topGameDataLoad,
    topGameTime,
    topGameTime2,
    topGameTime3,
    topGameTime4,
    topTotalGame,
    pcHasData,
    consoleHasData,
    topMobileGame10,
    topPCGame10,
    topConsoleGame10,
    topGenreData,
    topGenreDataLoad,
  };
}
